import { useState, useMemo, useCallback, useEffect } from 'react';
import { Panel } from '@xyflow/react';
import { Button } from 'src/components/ui/button';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { Badge } from 'src/components/ui/badge';
import { Separator } from 'src/components/ui/separator';
import {
  CaretLeftIcon,
  HammerIcon,
  GearSixIcon,
  SidebarIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';
import { NodeLibraryContent } from './NodeLibraryContent';
import { getConfigRenderer, type StepNode } from './config-renderers';

interface WorkflowEditorPanelProps {
  selectedNode: StepNode | null;
  onNodeDeselect: () => void;
  onConfigChange: (nodeId: string, newConfig: Record<string, unknown>) => void;
  initiallyCollapsed?: boolean;
}

export function WorkflowEditorPanel({
  selectedNode,
  onNodeDeselect,
  onConfigChange,
  initiallyCollapsed = false,
}: WorkflowEditorPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);
  const [searchQuery, setSearchQuery] = useState('');

  const nodeData = selectedNode?.data;

  const ConfigRenderer = useMemo(() => {
    if (!nodeData?.stepType) return null;
    return getConfigRenderer(nodeData.stepType);
  }, [nodeData?.stepType]);

  const handleConfigChange = useCallback(
    (newConfig: Record<string, unknown>) => {
      if (selectedNode?.id) {
        onConfigChange(selectedNode.id, newConfig);
      }
    },
    [selectedNode?.id, onConfigChange],
  );

  const handleBackClick = useCallback(() => {
    onNodeDeselect();
  }, [onNodeDeselect]);

  const stepType = nodeData?.stepType ?? 'unknown';
  const stepCategory = nodeData?._stepTypeData?.category ?? 'Utility';

  useEffect(() => {
    if (initiallyCollapsed) {
      setIsCollapsed(true);
    }
  }, [initiallyCollapsed]);

  if (isCollapsed) {
    return (
      <Panel position="top-right" className="m-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsCollapsed(false)}
          className="bg-background shadow-lg"
        >
          <SidebarIcon className="h-4 w-4" />
        </Button>
      </Panel>
    );
  }

  return (
    <Panel position="top-right" className="m-2">
      <div
        className={cn(
          'bg-background border rounded-lg shadow-lg flex flex-col overflow-hidden',
          'transition-all duration-200',
          selectedNode ? 'w-[37rem]' : 'w-[25rem]',
          'h-[calc(100vh-12rem)] max-h-[600px]',
        )}
      >
        <div className="flex items-center justify-between p-1.5 border-b flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {selectedNode && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={handleBackClick}
              >
                <CaretLeftIcon className="h-4 w-4" />
              </Button>
            )}

            {selectedNode && nodeData ? (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <GearSixIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium truncate">
                  {nodeData.label}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <HammerIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium">Step Library</span>
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={() => setIsCollapsed(true)}
          >
            <SidebarIcon className="h-4 w-4" />
          </Button>
        </div>

        {selectedNode && nodeData ? (
          <>
            <div className="p-3 border-b flex-shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  {stepCategory}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">
                  {stepType}
                </span>
              </div>
              {nodeData._stepTypeData?.description && (
                <p className="text-xs text-muted-foreground mt-2">
                  {nodeData._stepTypeData.description}
                </p>
              )}
            </div>

            <Separator />

            <ScrollArea className="flex-1 min-h-0 p-3">
              {ConfigRenderer && selectedNode && (
                <ConfigRenderer
                  node={selectedNode}
                  config={nodeData.config ?? {}}
                  onConfigChange={handleConfigChange}
                />
              )}
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 min-h-0 overflow-hidden">
            <NodeLibraryContent
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>
        )}
      </div>
    </Panel>
  );
}
